<template>
  <div
    ref="target"
    @keydown.esc="closeModal"
  >
    <slot
      name="default"
      :display="display"
    />

    <TWModal
      :loading="false"
      :show="show"
      @close="closeModal"
      :allow-outside-click-to-close="true"
    >
      <template v-slot:content>
        <WistiaVideo
          :mediaId="mediaId"
          :autoplay="true"
          :muted="muted"
          :showControls="showControls"
          :endBehavior="endBehavior"
          :altText="altText"
          :logEventCategory="logEventCategory"
        />
      </template>
    </TWModal>
  </div>
</template>

<script>
import { defineComponent, ref } from 'vue';
import TWModal from '@/components/tailwind/Modal.vue';
import { WistiaVideo } from '@/components/library/video/WistiaVideo';
import { createEventLogger } from '@/utils/analytics';
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap';

export default defineComponent({
  props: {
    mediaId: {
      type: String,
      required: true,
    },
    muted: {
      type: Boolean,
      required: false,
      default: false,
    },
    showControls: {
      type: Boolean,
      required: false,
      default: true,
    },
    /**
    * Set what this video should do when it reaches the end
    * 'default' - Stays on the last frame
    * 'reset'   - Displays thumbnail
    * 'loop'    - Loop the video
    */
    endBehavior: {
      type: String,
      required: false,
      default: 'reset',
      validator(value) {
        return ['default', 'reset', 'loop'].includes(value);
      },
    },
    altText: {
      type: String,
      required: false,
      default: '',
    },
    /**
     * This is the equivalent of data-ec. If it's empty,
     * nothing will be logged in this component.
     */
    logEventCategory: {
      type: String,
      required: false,
    },
    logVideoPlayEventName: {
      type: String,
      required: false,
      default: 'video_modal_autoplay',
    },
  },
  components: {
    TWModal,
    WistiaVideo,
  },
  setup(props) {
    const show = ref(false);
    const target = ref();

    const { activate, deactivate } = useFocusTrap(target);
    const logEvent = createEventLogger(props.logEventCategory);

    async function display() {
      show.value = true;
      activate();

      if (props.logEventCategory) {
        logEvent(props.logVideoPlayEventName, {
          mediaId: props.mediaId,
        });
      }
    }

    async function closeModal() {
      show.value = false;
      deactivate();
    }

    return {
      show,
      target,
      display,
      closeModal,
    };
  },
});
</script>
